/**
 * Everything a FORM card does with values that is not drawing them: prefilling,
 * which fields are visible, validating on submit, building the payload, picking the
 * budget range, and resolving a select whose options arrive late.
 *
 * Pure on purpose. A form has a lot of little rules and every one of them is a way
 * to silently send the backend something it will reject — they are unit tested
 * instead of clicked through.
 */
import { isValidDate, readableDate } from "../../wire";
import type { SelectOption } from "../../wire";
import type {
  FormActionError,
  FormErrors,
  FormFieldSpec,
  FormId,
  FormValue,
  FormValues,
  MoneyRange,
  OptionsStatus,
} from "./formTypes";

const REQUIRED = "Este campo es obligatorio.";

export const INVALID_PRICE = "Ingresa un precio mayor a 0.";

/** How the backend's refusal for a page without a price begins: the price box is to blame. */
const ASK_PRICE_ERROR_PREFIX = "Esa página no muestra el precio";

export const isAskPriceError = (error: string | undefined): boolean =>
  !!error && error.startsWith(ASK_PRICE_ERROR_PREFIX);

/** A typed price that is not worth sending: not a number above zero. */
export const isInvalidPrice = (price: string): boolean =>
  !!price && !(Number(price) > 0);

/** The prefill a field's `suggested` stands for, or undefined when it has none. */
const suggestedValue = (field: FormFieldSpec): FormValue | undefined => {
  const { suggested } = field;
  if (!suggested) return undefined;
  switch (field.kind) {
    case "toggle":
      return suggested === "true";
    case "money":
      return { amount: suggested.replace(/[^\d]/g, "") };
    case "multi_chips":
      return suggested.split(",").filter(Boolean);
    case "product":
      // The link the chat was started from: offered as a pasted link.
      return { url: suggested };
    default:
      return suggested;
  }
};

/**
 * The values a card opens with: the card's own suggestions, overlaid with whatever
 * was saved. Saved keys the card no longer has are dropped, so a draft written for
 * an older card shape cannot smuggle values the backend would ignore.
 */
export const initialValues = (
  fields: FormFieldSpec[],
  saved: FormValues = {},
): FormValues => {
  const values: FormValues = {};
  for (const field of fields) {
    const stored = saved[field.key];
    const value = stored !== undefined ? stored : suggestedValue(field);
    if (value !== undefined) values[field.key] = value;
  }
  return values;
};

/**
 * The brand-account field only means something while the post is a collaboration.
 * Keyed on the contract's stable field ids — the card carries no dependency
 * information of its own, and this is the one dependency the two forms have.
 */
export const isFieldVisible = (
  field: FormFieldSpec,
  values: FormValues,
): boolean => {
  if (field.key === "collaborationAccount") {
    return values.collaboration === true;
  }
  return true;
};

const asText = (value: FormValue | undefined): string =>
  typeof value === "string" ? value : "";

const asList = (value: FormValue | undefined): string[] =>
  Array.isArray(value) ? value : [];

export const moneyAmount = (value: FormValue | undefined): string => {
  if (typeof value !== "object" || Array.isArray(value) || value === null) {
    return "";
  }
  return "amount" in value ? value.amount : "";
};

export const isBarterOnly = (value: FormValue | undefined): boolean =>
  typeof value === "object" &&
  !Array.isArray(value) &&
  value !== null &&
  "barterOnly" in value;

/** A pasted link is worth sending when it parses as an http(s) URL with a host. */
export const isPlausibleUrl = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return url.hostname.includes(".");
  } catch {
    return false;
  }
};

export type ProductChoice =
  | { kind: "none" }
  | { kind: "catalogue"; productId: string }
  /** `price` is what the person typed for a page that does not show one; "" when none. */
  | { kind: "link"; url: string; price: string };

export const productChoice = (value: FormValue | undefined): ProductChoice => {
  if (typeof value !== "object" || Array.isArray(value) || value === null) {
    return { kind: "none" };
  }
  if ("productId" in value && value.productId) {
    return { kind: "catalogue", productId: value.productId };
  }
  if ("url" in value && value.url.trim()) {
    return {
      kind: "link",
      url: value.url.trim(),
      price:
        "price" in value ? (value.price ?? "").trim().replace(/\.$/, "") : "",
    };
  }
  return { kind: "none" };
};

/**
 * The value of a pasted link: `{url}`, or `{url, price}` once a price was typed.
 * No link means no value — a price alone is not a product.
 */
export const linkValue = (
  url: string,
  price: string,
): FormValue | undefined => {
  if (!url.trim()) return undefined;
  return price ? { url, price } : { url };
};

const isEmpty = (
  field: FormFieldSpec,
  value: FormValue | undefined,
): boolean => {
  switch (field.kind) {
    case "product":
      return productChoice(value).kind === "none";
    case "multi_chips":
      return asList(value).length === 0;
    case "money":
      return !isBarterOnly(value) && !moneyAmount(value);
    case "toggle":
      return false;
    default:
      return !asText(value).trim();
  }
};

const validateField = (
  field: FormFieldSpec,
  value: FormValue | undefined,
): string | null => {
  if (isEmpty(field, value)) {
    if (!field.required) return null;
    switch (field.kind) {
      case "product":
        return "Elige un producto o pega el link de su página.";
      case "multi_chips":
        return "Elige al menos una opción.";
      case "single_select":
        return "Elige una opción.";
      case "money":
        return field.allowBarter
          ? "Ingresa un monto o marca “Solo canje”."
          : "Ingresa un monto.";
      case "date":
        return "Elige una fecha.";
      default:
        return REQUIRED;
    }
  }

  switch (field.kind) {
    case "product": {
      const choice = productChoice(value);
      if (choice.kind === "link" && !isPlausibleUrl(choice.url)) {
        return "Ese link no parece válido. Pega la dirección completa (https://…).";
      }
      if (choice.kind === "link" && isInvalidPrice(choice.price)) {
        return INVALID_PRICE;
      }
      return null;
    }
    case "multi_chips": {
      const count = asList(value).length;
      const least = Math.max(field.minSelections, field.required ? 1 : 0);
      if (count < least) {
        return least === 1
          ? "Elige al menos una opción."
          : `Elige al menos ${least}.`;
      }
      if (field.maxSelections !== null && count > field.maxSelections) {
        return `Elige máximo ${field.maxSelections}.`;
      }
      return null;
    }
    case "money": {
      if (isBarterOnly(value)) return null;
      return Number(moneyAmount(value)) > 0
        ? null
        : "Ingresa un monto mayor a 0.";
    }
    case "date": {
      const text = asText(value);
      if (!isValidDate(text)) return "La fecha no es válida.";
      if (field.minDate && text < field.minDate) {
        return `No puede ser antes del ${readableDate(field.minDate)}.`;
      }
      if (field.maxDate && text > field.maxDate) {
        return `No puede ser después del ${readableDate(field.maxDate)}.`;
      }
      return null;
    }
    default:
      return null;
  }
};

/** Field key -> message, for every visible field that would be refused. */
export const validateForm = (
  fields: FormFieldSpec[],
  values: FormValues,
): FormErrors => {
  const errors: FormErrors = {};
  for (const field of fields) {
    if (!isFieldVisible(field, values)) continue;
    const message = validateField(field, values[field.key]);
    if (message) errors[field.key] = message;
  }
  return errors;
};

/**
 * Split the backend's refusals into the ones a field can show and the one the form
 * shows. Only a field the person can see counts as known: an error routed to a hidden
 * field would never be drawn and the submit would fail without a word.
 */
export const routeActionErrors = (
  fields: FormFieldSpec[],
  values: FormValues,
  errors: FormActionError[],
): { fieldErrors: FormErrors; general: string | null } => {
  const visible = new Set(
    fields
      .filter((field) => isFieldVisible(field, values))
      .map((field) => field.key),
  );
  const fieldErrors: FormErrors = {};
  let general: string | null = null;
  for (const error of errors) {
    if (error.field && visible.has(error.field)) {
      fieldErrors[error.field] ??= error.message;
    } else {
      general ??= error.message;
    }
  }
  return { fieldErrors, general };
};

/** The key of the first field, in card order, that has an error. */
export const firstInvalidKey = (
  fields: FormFieldSpec[],
  errors: FormErrors,
): string | null => fields.find((field) => errors[field.key])?.key ?? null;

/**
 * The `values` of a form submit action: visible fields only, trimmed, with empty
 * optional fields left out entirely (the backend defaults them) and the money
 * field reduced to exactly one of `{amount}` / `{barterOnly}`.
 */
export const toSubmitValues = (
  fields: FormFieldSpec[],
  values: FormValues,
): FormValues => {
  const payload: FormValues = {};
  for (const field of fields) {
    if (!isFieldVisible(field, values)) continue;
    const value = values[field.key];
    switch (field.kind) {
      case "toggle":
        payload[field.key] = value === true;
        break;
      case "multi_chips": {
        const list = asList(value);
        if (list.length) payload[field.key] = list;
        break;
      }
      case "money": {
        if (isBarterOnly(value)) payload[field.key] = { barterOnly: true };
        else if (moneyAmount(value)) {
          payload[field.key] = { amount: moneyAmount(value) };
        }
        break;
      }
      case "product": {
        const choice = productChoice(value);
        if (choice.kind === "catalogue") {
          payload[field.key] = { productId: choice.productId };
        } else if (choice.kind === "link") {
          payload[field.key] = choice.price
            ? { url: choice.url, price: choice.price }
            : { url: choice.url };
        }
        break;
      }
      default: {
        const text = asText(value).trim();
        if (text) payload[field.key] = text;
      }
    }
  }
  return payload;
};

/** The range of a money field for the value its `rangeBy` field currently holds. */
export const rangeFor = (
  field: FormFieldSpec,
  values: FormValues,
): MoneyRange | null => {
  if (!field.rangeBy) return null;
  const selected = values[field.rangeBy];
  if (typeof selected !== "string") return null;
  return field.ranges.find((range) => range.when === selected) ?? null;
};

/** Where a card's unsubmitted values are kept, per session and card. */
export const draftStorageKey = (sessionId: string, formId: FormId): string =>
  `agent-form:${sessionId}:${formId}`;

export const serializeDraft = (values: FormValues): string =>
  JSON.stringify(values);

/**
 * A select whose options are computed after the card is shown (`optionsStatus` is
 * not "ready" on the card). Its options come from a `FORM_OPTIONS` event, so what the
 * field shows depends on a request the client made and an answer that may not come.
 */
export type OptionsRequest = {
  /** The event count for this field at the moment the request went out. */
  seqAtRequest: number;
  /**
   * What the options are being computed for: a catalogue product's id or a link,
   * exactly one set. An answer only counts if it names the same one — the person may
   * swap the product while an earlier request is still being answered.
   */
  productId: string | null;
  url: string | null;
  failed: boolean;
  timedOut: boolean;
};

export type LiveOptions = {
  options: SelectOption[];
  status: OptionsStatus;
  /** Bumped by every event for this field, so a fresh answer can be told from an old one. */
  seq: number;
  /** The run that computed them, and the product (or link) it computed them for. */
  runId: string;
  productId: string | null;
  url: string | null;
};

export type ResolvedOptions = {
  /** `idle`: nothing asked for yet. `loading`: asked, waiting. */
  status: "idle" | "loading" | "ready" | "failed";
  options: SelectOption[];
};

/**
 * Whether `live` is the answer to `request`: newer than it, and about the product the
 * field holds now. Options of a product that has since been replaced are dropped, not
 * shown under the new one.
 */
const answersRequest = (live: LiveOptions, request: OptionsRequest): boolean =>
  live.seq > request.seqAtRequest &&
  live.productId === request.productId &&
  live.url === request.url;

export const resolveOptions = (
  field: FormFieldSpec,
  live: LiveOptions | undefined,
  request: OptionsRequest | null,
): ResolvedOptions => {
  if (field.optionsStatus === "ready") {
    return { status: "ready", options: field.options };
  }
  if (!request) return { status: "idle", options: [] };

  const fresh = live && answersRequest(live, request) ? live : null;
  if (fresh?.status === "ready") {
    return { status: "ready", options: fresh.options };
  }
  if (fresh?.status === "failed" || request.failed || request.timedOut) {
    return { status: "failed", options: [] };
  }
  return { status: "loading", options: [] };
};

/**
 * "Producto: X\nPlataformas: A, B" -> label/value pairs. A line with no colon is
 * kept whole as a value, so a summary we cannot split still reads.
 */
export const parseSummaryLines = (
  text: string,
): { label: string | null; value: string }[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const at = line.indexOf(": ");
      if (at <= 0) return { label: null, value: line };
      return { label: line.slice(0, at), value: line.slice(at + 2) };
    });

/**
 * Lay the visible fields out in rows: a full-width field is a row of its own, and
 * consecutive half-width fields share one (two at most — a third half would be a
 * sliver). A half field with no neighbour keeps a row to itself.
 */
export const groupRows = (fields: FormFieldSpec[]): FormFieldSpec[][] => {
  const rows: FormFieldSpec[][] = [];
  for (const field of fields) {
    const last = rows.at(-1);
    if (
      field.width === "half" &&
      last?.length === 1 &&
      last[0]?.width === "half"
    ) {
      last.push(field);
    } else {
      rows.push([field]);
    }
  }
  return rows;
};
